import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../services/authService';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  seller: string;
}

const CryptoCheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [bitcoinAddress, setBitcoinAddress] = useState<string>('bc1q8z6l3ehp5w74ztmyd7q42qmz7j0g87fzrj25wd');
  const [btcAmount, setBtcAmount] = useState<number>(0);
  const [countdown, setCountdown] = useState<number>(900); // 15 minutes in seconds

  useEffect(() => {
    // Check authentication first
    if (!isAuthenticated()) {
      navigate('/login', { state: { from: location } });
      return;
    }

    setIsLoading(true);

    // Only proceed if authenticated
    try {
      // Get cart items from location state
      const items = location.state?.items || [];
      if (!items.length) {
        navigate('/cart');
        return;
      }

      setCartItems(items);

      // Calculate total
      const total = items.reduce((sum: number, item: CartItem) => sum + item.price, 0);
      setTotalAmount(total);

      // Set BTC amount (this would normally come from an API that gives current exchange rate)
      const btcRate = 0.000016; // Example rate: 1 USD = 0.000016 BTC
      setBtcAmount(+(total * btcRate).toFixed(8));

      setIsLoading(false);

      // Start countdown
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(timer);
            // Handle expired time
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } catch (error) {
      console.error('Error in CryptoCheckoutPage:', error);
      setIsLoading(false);
    }
  }, [location, navigate]);

  // Format countdown as mm:ss
  const formatCountdown = () => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">Crypto Checkout</h1>

        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <div className="text-center mb-6">
            <p className="text-lg mb-2">Total Amount:</p>
            <p className="text-3xl font-bold text-purple-500">${totalAmount.toFixed(2)}</p>
            <p className="text-lg font-medium">= {btcAmount} BTC</p>
          </div>

          <div className="flex flex-col items-center mb-6">
            <p className="mb-4 font-medium">Send exactly {btcAmount} BTC to:</p>
            <div className="bg-white p-4 rounded-lg mb-4">
              {/* QR code would be here - using placeholder for now */}
              <div className="w-[200px] h-[200px] bg-gray-200 flex items-center justify-center text-gray-500">
                QR Code for Bitcoin Address
              </div>
            </div>
            <div className="bg-gray-700 py-2 px-4 rounded-lg mb-2 w-full text-center break-all">
              {bitcoinAddress}
            </div>
            <button
              className="text-purple-400 text-sm"
              onClick={() => navigator.clipboard.writeText(bitcoinAddress)}
            >
              Copy address
            </button>
          </div>

          <div className="text-center mb-6">
            <p className="text-amber-400 mb-2">This offer expires in:</p>
            <p className="text-2xl font-bold">{formatCountdown()}</p>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
            <p className="text-amber-400 text-sm">
              Please send the exact amount in one transaction. After we receive your payment, your order will be processed.
              This usually takes 1-2 confirmations on the blockchain.
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/orders')}
              className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-6 rounded-lg transition-colors"
            >
              Check Order Status
            </button>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Order Summary</h2>

          <div className="space-y-4 mb-6">
            {cartItems.map((item) => (
              <div key={item.id} className="flex items-center">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-16 h-16 object-cover rounded-md mr-4"
                />
                <div className="flex-grow">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-gray-400 text-sm">by {item.seller}</p>
                </div>
                <p className="font-medium">${item.price.toFixed(2)}</p>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-700 pt-4">
            <div className="flex justify-between mb-2">
              <p>Subtotal</p>
              <p>${totalAmount.toFixed(2)}</p>
            </div>
            <div className="flex justify-between mb-2">
              <p>Processing Fee</p>
              <p>$0.00</p>
            </div>
            <div className="flex justify-between font-bold text-lg">
              <p>Total</p>
              <p>${totalAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoCheckoutPage;
